import { Validators } from '@angular/forms';

import map from 'lodash/map';
import extend from 'lodash/extend';

import { IGeoLocation } from '@cakeiteasy/web-utils';

import { UI_DELIVERY_OPTIONS } from '../cart/constants/common';
import { IDeliveryAddress } from '../users/models/i-delivery-address';

import { IBakeryDeliveryInfo, ThirdPartyLocation } from './models';

export const isGeoLocationValid = (geoLocation: IGeoLocation | null, strict = true): boolean => {
  const {
    placeName,
    address: { streetName, city, streetNumber },
    location: { lat, lng },
  } = geoLocation;
  const geoData: (string | number)[] = [placeName || city, streetName, streetNumber];

  if (strict) {
    geoData.push(lat, lng);
  }

  return geoData.every(Boolean);
};

export const mapDeliveryToLookupAddress = ({ address, postNumber, cityName }: IDeliveryAddress): string => {
  const postNumberCity = [postNumber, cityName].filter(Boolean).join(' ');
  return [address, postNumberCity].filter(Boolean).join(', ');
};

export const getFilledDeliveryFormLocation = (geoLocation: IGeoLocation) => {
  const { postNumber, placeName, location: { lng, lat }, address: { city, streetName, streetNumber } } = geoLocation;

  return {
    deliveryCity: placeName || city,
    deliveryAddress: [streetName, streetNumber].filter(Boolean).join(' '),
    deliveryPostNumber: postNumber,
    deliveryLatitude: lat,
    deliveryLongitude: lng,
  };
};

export function concatOutletWithDeliveryInfo(outlets, cartDelivery) {
  return map(outlets, (o) => {
    if (o.id === cartDelivery.outletId) {
      extend(o, { date: cartDelivery.date, timeRange: cartDelivery.timeRange });
    }

    return o;
  });
}

export function getDeliveryBlockedOptions(deliveryInfo: IBakeryDeliveryInfo,
                                          availabilityOptions: {
                                            isPickUpAvailable?: boolean;
                                            isToDoorAvailable?: boolean;
                                            isMailToDoorAvailable?: boolean;
                                          }): UI_DELIVERY_OPTIONS[] {
  const blocked = [];
  const { PICKUP, DELIVERY, MAIL } = UI_DELIVERY_OPTIONS;
  const { outletsExists, deliveryPointsExists, mailDeliveryPointsExists, thirdPartyDeliveryAllowed } = deliveryInfo;
  const { isPickUpAvailable, isToDoorAvailable, isMailToDoorAvailable } = availabilityOptions;

  if (!outletsExists || (outletsExists && !isPickUpAvailable)) {
    blocked.push(PICKUP);
  }

  if (!isToDoorAvailable || !deliveryPointsExists && !thirdPartyDeliveryAllowed) {
    blocked.push(DELIVERY);
  }

  if (mailDeliveryPointsExists && !isMailToDoorAvailable) {
    blocked.push(MAIL);
  }

  return blocked;
}

export function getInitialDeliveryType(params: {
                                         outletsExists: boolean,
                                         mailDeliveryPointsExists: boolean,
                                         deliveryPointsExists: boolean,
                                         isGiftDelivery: boolean,
                                         thirdPartyDeliveryAllowed: boolean,
                                       },
                                       blockedOptions: UI_DELIVERY_OPTIONS[]) {
  const deliveryOptions = getDeliveryOptions(params);
  const availableOptions = deliveryOptions.filter(o => blockedOptions.indexOf(o) === -1);

  if (availableOptions.length === 1) {
    return availableOptions[0];
  }

  return null;
}

export function getDeliveryOptions(params: {
  outletsExists: boolean,
  mailDeliveryPointsExists: boolean,
  deliveryPointsExists: boolean,
  isGiftDelivery: boolean,
  thirdPartyDeliveryAllowed: boolean,
}): UI_DELIVERY_OPTIONS[] {
  const {
    outletsExists,
    mailDeliveryPointsExists,
    deliveryPointsExists,
    isGiftDelivery,
    thirdPartyDeliveryAllowed,
  } = params;
  const { PICKUP, DELIVERY, MAIL, GIFT } = UI_DELIVERY_OPTIONS;

  const options: UI_DELIVERY_OPTIONS[] = [];
  if (outletsExists) {
    options.push(PICKUP);
  }

  if (deliveryPointsExists || thirdPartyDeliveryAllowed) {
    options.push(DELIVERY);
  }

  if (isGiftDelivery) {
    options.push(GIFT);
  }

  if (mailDeliveryPointsExists) {
    options.push(MAIL);
  }

  return options;
}

export const getDeliveryFormGroup = () => {
  return {
    deliveryCity: ['', [Validators.required]],
    deliveryAddress: ['', [Validators.required]],
    deliveryPostNumber: ['', [Validators.required]],
    deliveryDate: [null, [Validators.required]],
    deliveryPhone: ['', [Validators.required]],
    deliveryTime: ['', [Validators.required]],
    deliveryUserName: ['', [Validators.required]],
    deliveryLatitude: ['', [Validators.required]],
    deliveryLongitude: ['', [Validators.required]],
    deliveryNote: [''],
    deliveryAgent: [null],
  };
};

export const getEmptyToDoorLocation = () => {
  return {
    deliveryCity: '',
    deliveryAddress: '',
    deliveryPostNumber: '',
    deliveryLatitude: '',
    deliveryLongitude: '',
  };
};

export const mapGeoLocationToThirdPartyLocation = (geoLocation: IGeoLocation): ThirdPartyLocation => {
  const { location: { lat, lng: lon }, address } = geoLocation;
  const formattedAddress = receiveFormatedAddress(address);

  return { formattedAddress, coordinates: { lat, lon } };
};

export const receiveFormatedAddress = ({ streetName, streetNumber, city }: {
  streetName: string,
  streetNumber: string,
  city: string,
}): string => {
  const street = [streetName, streetNumber].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ');
};

export const receiveWoltErrorTransKeys = (errorCode: string): { titleKey: string, messageKey: string } | null => {
  return {
    titleKey: `woltErrors.${errorCode}.title`,
    messageKey: `woltErrors.${errorCode}.message`,
  };
};
